import { apiClient } from '../client/apiClient';
import { fetchCsrfToken } from '../client/fetchCsrfToken';

// ============================================================================
// Types
// ============================================================================

export type NotificationDeviceGroup = 'EMAIL' | 'NATIVE_MOBILE' | 'BROWSER';
export type NotificationTypeGroup = 'REC_SEARCH' | 'SAVED_SEARCH' | 'PROPERTY_UPDATE';

export type NotificationSetting = {
  deviceGroup: NotificationDeviceGroup;
  typeGroup: NotificationTypeGroup;
  optedOut: boolean;
};

export type NotificationSettingsResponse = {
  success: boolean;
  data: Array<NotificationSetting>;
};

export type EmailSubscriptions = {
  stopListings: boolean;
  stopOther: boolean;
};

export type EmailSubscriptionsResponse = {
  success: boolean;
  data: EmailSubscriptions;
};

export type NotificationPreferences = {
  email: boolean;
  mobile: boolean;
};

export type AllNotificationPreferences = {
  recSearch: NotificationPreferences;
  savedSearch: NotificationPreferences;
  propertyUpdate: NotificationPreferences;
};

// ============================================================================
// Helpers
// ============================================================================

const DEVICE_MAP: Partial<Record<NotificationDeviceGroup, keyof NotificationPreferences>> = {
  EMAIL: 'email',
  NATIVE_MOBILE: 'mobile',
};

export function parseNotificationSettings(
  settings: Array<NotificationSetting>,
): AllNotificationPreferences {
  const prefs: AllNotificationPreferences = {
    recSearch: { email: true, mobile: true },
    savedSearch: { email: true, mobile: true },
    propertyUpdate: { email: true, mobile: true },
  };

  for (const { deviceGroup, typeGroup, optedOut } of settings) {
    const device = DEVICE_MAP[deviceGroup];
    if (!device) continue;

    if (typeGroup === 'REC_SEARCH') prefs.recSearch[device] = !optedOut;
    else if (typeGroup === 'SAVED_SEARCH') prefs.savedSearch[device] = !optedOut;
    else if (typeGroup === 'PROPERTY_UPDATE') prefs.propertyUpdate[device] = !optedOut;
  }

  return prefs;
}

// ============================================================================
// Endpoints
// ============================================================================

export async function getNotificationSettings(): Promise<Array<NotificationSetting>> {
  const response = await apiClient<NotificationSettingsResponse>({
    path: '/v2/notifications/settings/getAll',
  });
  return response.data ?? [];
}

export async function updateNotificationSettings(
  options: Array<NotificationSetting>,
): Promise<void> {
  const csrfToken = await fetchCsrfToken();
  const headers: Record<string, string> = {};
  if (csrfToken) {
    headers.csrfToken = csrfToken;
  }

  await apiClient<unknown>({
    method: 'POST',
    path: '/v2/notifications/settings/update',
    body: options,
    headers,
  });
}

export async function getEmailSubscriptions(email: string): Promise<EmailSubscriptions> {
  const response = await apiClient<EmailSubscriptionsResponse>({
    path: '/v2/email/optOut/list',
    queryParams: { email },
  });
  return response.data ?? { stopListings: false, stopOther: false };
}

export async function updateEmailSubscriptions(
  subscriptions: EmailSubscriptions & { email: string },
): Promise<void> {
  const csrfToken = await fetchCsrfToken();
  const headers: Record<string, string> = {};
  if (csrfToken) {
    headers.csrfToken = csrfToken;
  }

  await apiClient<unknown>({
    method: 'PUT',
    path: '/v2/email/optOut/update',
    body: subscriptions,
    headers,
  });
}

export type CancelEmailSearchesBody = {
  cancelHash: string;
  email: string;
  cancelSavedSearch?: boolean;
};

export async function cancelEmailSearches(body: CancelEmailSearchesBody): Promise<void> {
  const csrfToken = await fetchCsrfToken();
  const headers: Record<string, string> = {};
  if (csrfToken) {
    headers.csrfToken = csrfToken;
  }

  await apiClient<unknown>({
    method: 'PUT',
    path: '/v2/email/cancelSearches',
    body,
    headers,
  });
}

export type UpdateSearchFrequencyBody = {
  email: string;
  frequency: string;
  hash: string;
  searchId: string;
};

export type UpdateSearchFrequencyResponse = {
  success?: boolean;
  data?: {
    searchName?: string;
  };
};

export async function updateSearchFrequency(
  body: UpdateSearchFrequencyBody,
): Promise<UpdateSearchFrequencyResponse> {
  const csrfToken = await fetchCsrfToken();
  const headers: Record<string, string> = {};
  if (csrfToken) {
    headers.csrfToken = csrfToken;
  }

  return apiClient<UpdateSearchFrequencyResponse>({
    method: 'PUT',
    path: '/v2/user/updateSearchFrequency',
    body,
    headers,
  });
}

// ============================================================================
// Namespace Export
// ============================================================================

export const notifications = {
  getSettings: getNotificationSettings,
  updateSettings: updateNotificationSettings,
  getEmailSubscriptions,
  updateEmailSubscriptions,
  cancelEmailSearches,
  updateSearchFrequency,
};
